import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { CartDrawer } from "@/components/cart/CartDrawer";
import { ScrollToTop } from "@/components/ScrollToTop";
import { TrackingPixels } from "@/components/TrackingPixels";
import { Loader2 } from "lucide-react";

// Lazy-loaded pages
const Index = lazy(() => import("./pages/Index"));
const Courses = lazy(() => import("./pages/Courses"));
const CourseDetail = lazy(() => import("./pages/CourseDetail"));
const CoursePlayer = lazy(() => import("./pages/CoursePlayer"));
const Products = lazy(() => import("./pages/Products"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Login = lazy(() => import("./pages/auth/Login"));
const Signup = lazy(() => import("./pages/auth/Signup"));
const Training = lazy(() => import("./pages/Training"));
const TrainingDetail = lazy(() => import("./pages/TrainingDetail"));
// SurvivalKit now served via dynamic landing pages at /lp/survival-kit
const Membership = lazy(() => import("./pages/Membership"));
const MemberBenefits = lazy(() => import("./pages/MemberBenefits"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Welcome = lazy(() => import("./pages/Welcome"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const PricingAgreement = lazy(() => import("./pages/PricingAgreement"));
const Certificates = lazy(() => import("./pages/Certificates"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin Pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminCourses = lazy(() => import("./pages/admin/AdminCourses"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminSubscriptions = lazy(() => import("./pages/admin/AdminSubscriptions"));
const CourseEditor = lazy(() => import("./pages/admin/CourseEditor"));
const ModuleEditor = lazy(() => import("./pages/admin/ModuleEditor"));
const CourseBuilder = lazy(() => import("./pages/admin/CourseBuilder"));
const AdminTraining = lazy(() => import("./pages/admin/AdminTraining"));
const AdminSubscribers = lazy(() => import("./pages/admin/AdminSubscribers"));
const LessonEditor = lazy(() => import("./pages/admin/LessonEditor"));
const AdminLandingPages = lazy(() => import("./pages/admin/AdminLandingPages"));
const AdminTracking = lazy(() => import("./pages/admin/AdminTracking"));
const AdminVideos = lazy(() => import("./pages/admin/AdminVideos"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminApiDocs = lazy(() => import("./pages/admin/AdminApiDocs"));
const AdminQuotes = lazy(() => import("./pages/admin/AdminQuotes"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const LandingPageThankYou = lazy(() => import("./pages/LandingPageThankYou"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <CartProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <TrackingPixels />
            <CartDrawer />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Index />} />
                <Route path="/courses" element={<Courses />} />
                <Route path="/courses/:id" element={<CourseDetail />} />
                <Route path="/learn/:id" element={<ProtectedRoute><CoursePlayer /></ProtectedRoute>} />
                <Route path="/learn/:id/:lessonId" element={<ProtectedRoute><CoursePlayer /></ProtectedRoute>} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/:id" element={<ProductDetail />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/training" element={<Training />} />
                <Route path="/training/:id" element={<TrainingDetail />} />
                <Route path="/survival-kit" element={<LandingPage />} />
                <Route path="/lp/:slug" element={<LandingPage />} />
                <Route path="/lp/:slug/thank-you" element={<LandingPageThankYou />} />
                <Route path="/membership" element={<Membership />} />
                <Route path="/member-benefits" element={<MemberBenefits />} />
                <Route path="/thank-you" element={<ThankYou />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/welcome" element={<Welcome />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/cookies" element={<CookiePolicy />} />
                <Route path="/map-agreement" element={<PricingAgreement />} />
                <Route path="/certificates" element={<Certificates />} />
                
                {/* Auth Routes */}
                <Route path="/auth/login" element={<Login />} />
                <Route path="/auth/signup" element={<Signup />} />
                
                {/* Protected Routes */}
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />

                {/* Admin Routes */}
                <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/courses" element={<ProtectedRoute requireAdmin><AdminCourses /></ProtectedRoute>} />
                <Route path="/admin/users" element={<ProtectedRoute requireAdmin><AdminUsers /></ProtectedRoute>} />
                <Route path="/admin/products" element={<ProtectedRoute requireAdmin><AdminProducts /></ProtectedRoute>} />
                <Route path="/admin/subscriptions" element={<ProtectedRoute requireAdmin><AdminSubscriptions /></ProtectedRoute>} />
                <Route path="/admin/course-builder" element={<ProtectedRoute requireAdmin><CourseBuilder /></ProtectedRoute>} />
                <Route path="/admin/courses/:id" element={<ProtectedRoute requireAdmin><CourseEditor /></ProtectedRoute>} />
                <Route path="/admin/courses/:id/modules" element={<ProtectedRoute requireAdmin><ModuleEditor /></ProtectedRoute>} />
                <Route path="/admin/courses/:id/lessons/:lessonId" element={<ProtectedRoute requireAdmin><LessonEditor /></ProtectedRoute>} />
                <Route path="/admin/training" element={<ProtectedRoute requireAdmin><AdminTraining /></ProtectedRoute>} />
                <Route path="/admin/subscribers" element={<ProtectedRoute requireAdmin><AdminSubscribers /></ProtectedRoute>} />
                <Route path="/admin/landing-pages" element={<ProtectedRoute requireAdmin><AdminLandingPages /></ProtectedRoute>} />
                <Route path="/admin/tracking" element={<ProtectedRoute requireAdmin><AdminTracking /></ProtectedRoute>} />
                <Route path="/admin/videos" element={<ProtectedRoute requireAdmin><AdminVideos /></ProtectedRoute>} />
                <Route path="/admin/analytics" element={<ProtectedRoute requireAdmin><AdminAnalytics /></ProtectedRoute>} />
                <Route path="/admin/api-docs" element={<ProtectedRoute requireAdmin><AdminApiDocs /></ProtectedRoute>} />
                <Route path="/admin/quotes" element={<ProtectedRoute requireAdmin><AdminQuotes /></ProtectedRoute>} />
                
                {/* Catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </CartProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
